import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { Admin } from "../lib/api";

// ============================================================================
// Types
// ============================================================================

interface SecurityStats {
  rateLimitToday: number;
  suspiciousIps: number;
  failedLoginsToday: number;
  adminActionsToday: number;
}

interface IpLogEntry {
  _id: string;
  ip: string;
  userId?: string | null;
  username?: string | null;
  action: string;
  endpoint: string;
  statusCode: number;
  userAgent?: string | null;
  timestamp: string;
  metadata?: Record<string, unknown> | null;
}

interface SuspiciousIp {
  _id: string;
  ip: string;
  suspicionScore: number;
  firstSeen: string;
  lastSeen: string;
  requestCount: number;
  failedAttempts: number;
  blacklisted: boolean;
  whitelisted: boolean;
  notes?: string | null;
}

interface RateLimitEvent {
  _id: string;
  ip: string;
  userId?: string | null;
  username?: string | null;
  endpoint: string;
  limitType: string;
  timestamp: string;
  metadata?: Record<string, unknown> | null;
}

interface SystemAlert {
  _id: string;
  type: string;
  severity: "low" | "medium" | "high" | "critical";
  message: string;
  details?: Record<string, unknown> | null;
  resolved: boolean;
  timestamp: string;
}

type TabType = "ip-log" | "suspicious" | "rate-limit" | "alerts";

// ============================================================================
// Main Component
// ============================================================================

export default function AdminSecurity() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("ip-log");

  // Stats
  const [stats, setStats] = useState<SecurityStats>({
    rateLimitToday: 0,
    suspiciousIps: 0,
    failedLoginsToday: 0,
    adminActionsToday: 0,
  });

  // Data
  const [ipLogs, setIpLogs] = useState<IpLogEntry[]>([]);
  const [suspiciousIps, setSuspiciousIps] = useState<SuspiciousIp[]>([]);
  const [rateLimitEvents, setRateLimitEvents] = useState<RateLimitEvent[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<IpLogEntry | SuspiciousIp | RateLimitEvent | SystemAlert | null>(null);

  // Check admin permission
  if (!user?.isAdmin) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h1>🚫 Erişim Engellendi</h1>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  async function loadStats() {
    try {
      const response = await Admin.getSecurityStats();
      if (response?.ok && response?.stats) {
        setStats({
          rateLimitToday: response.stats.rateLimitToday || 0,
          suspiciousIps: response.stats.suspiciousIps || 0,
          failedLoginsToday: response.stats.failedLoginsToday || 0,
          adminActionsToday: response.stats.adminActionsToday || 0,
        });
      }
    } catch (e: any) {
      console.error("Security stats load failed:", e);
    }
  }

  async function loadIpLogs() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.getSecurityIpLog({ limit: 100 });
      if (response?.ok && response?.logs) {
        setIpLogs(response.logs);
      } else if (Array.isArray(response)) {
        setIpLogs(response);
      }
    } catch (e: any) {
      console.error("IP logs load failed:", e);
      setError(e?.message || "IP logları yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadSuspiciousIps() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.getSecuritySuspicious({ minScore: 5, limit: 100 });
      if (response?.ok && response?.items) {
        setSuspiciousIps(response.items);
      } else if (Array.isArray(response)) {
        setSuspiciousIps(response);
      }
    } catch (e: any) {
      console.error("Suspicious IPs load failed:", e);
      setError(e?.message || "Şüpheli IP'ler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadRateLimitEvents() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.getSecurityRateLimitLog({ limit: 100 });
      if (response?.ok && response?.events) {
        setRateLimitEvents(response.events);
      } else if (Array.isArray(response)) {
        setRateLimitEvents(response);
      }
    } catch (e: any) {
      console.error("Rate limit events load failed:", e);
      setError(e?.message || "Rate limit olayları yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadSystemAlerts() {
    try {
      setLoading(true);
      setError(null);
      // Mock data for demonstration (backend endpoint not implemented yet)
      setSystemAlerts([
        {
          _id: "1",
          type: "rate_limit_spike",
          severity: "medium",
          message: "Rate limit olaylarında artış tespit edildi",
          timestamp: new Date().toISOString(),
          resolved: false,
        },
      ]);
    } catch (e: any) {
      console.error("System alerts load failed:", e);
      setError(e?.message || "Sistem uyarıları yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadData() {
    await loadStats();
    switch (activeTab) {
      case "ip-log":
        await loadIpLogs();
        break;
      case "suspicious":
        await loadSuspiciousIps();
        break;
      case "rate-limit":
        await loadRateLimitEvents();
        break;
      case "alerts":
        await loadSystemAlerts();
        break;
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      switch (activeTab) {
        case "ip-log":
          return ipLogs;
        case "suspicious":
          return suspiciousIps;
        case "rate-limit":
          return rateLimitEvents;
        case "alerts":
          return systemAlerts;
      }
    }

    switch (activeTab) {
      case "ip-log":
        return ipLogs.filter(
          (log) =>
            log.ip.includes(query) ||
            log.username?.toLowerCase().includes(query) ||
            log.action.toLowerCase().includes(query) ||
            log.endpoint.toLowerCase().includes(query)
        );
      case "suspicious":
        return suspiciousIps.filter((ip) => ip.ip.includes(query) || ip.notes?.toLowerCase().includes(query));
      case "rate-limit":
        return rateLimitEvents.filter(
          (event) =>
            event.ip.includes(query) ||
            event.username?.toLowerCase().includes(query) ||
            event.endpoint.toLowerCase().includes(query) ||
            event.limitType.toLowerCase().includes(query)
        );
      case "alerts":
        return systemAlerts.filter(
          (alert) =>
            alert.type.toLowerCase().includes(query) ||
            alert.message.toLowerCase().includes(query) ||
            alert.severity.toLowerCase().includes(query)
        );
    }
  }, [activeTab, searchQuery, ipLogs, suspiciousIps, rateLimitEvents, systemAlerts]);

  function formatDate(dateStr?: string | null) {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("tr-TR");
    } catch {
      return dateStr;
    }
  }

  function getSeverityBadge(severity: string) {
    const styles: Record<string, { bg: string; color: string; border: string }> = {
      low: { bg: "rgba(59, 130, 246, 0.1)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.4)" },
      medium: { bg: "rgba(251, 191, 36, 0.1)", color: "#fbbf24", border: "1px solid rgba(251, 191, 36, 0.4)" },
      high: { bg: "rgba(251, 113, 133, 0.1)", color: "#fb7185", border: "1px solid rgba(251, 113, 133, 0.4)" },
      critical: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.4)" },
    };
    const style = styles[severity] || styles.low;
    return (
      <span
        style={{
          ...style,
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {severity.toUpperCase()}
      </span>
    );
  }

  // Loading state
  if (loading && filteredData.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <p style={{ color: "var(--muted)" }}>Security verileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "var(--text)" }}>🛡️ Security Dashboard</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => loadData()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(59, 130, 246, 0.4)",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              color: "#3b82f6",
              cursor: "pointer",
            }}
          >
            <span>🔄</span>
            <span>Yenile</span>
          </button>
          <button
            onClick={() => navigate("/settings")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(156, 163, 175, 0.4)",
              backgroundColor: "rgba(156, 163, 175, 0.1)",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            <span>←</span>
            <span>Geri</span>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            borderRadius: 10,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: "#ef4444" }}>Veri yüklenemedi</div>
              <div style={{ fontSize: 14, color: "#ef4444", opacity: 0.9 }}>{error}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => loadData()}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(239, 68, 68, 0.4)",
                backgroundColor: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              🔄 Yeniden Dene
            </button>
            <button
              onClick={() => setError(null)}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(156, 163, 175, 0.4)",
                backgroundColor: "rgba(156, 163, 175, 0.1)",
                color: "#9ca3af",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(239, 68, 68, 0.4)",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#ef4444", marginBottom: 4 }}>{stats.rateLimitToday}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#ef4444", opacity: 0.8 }}>Rate Limit (Bugün)</div>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(234, 179, 8, 0.4)",
            backgroundColor: "rgba(234, 179, 8, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#eab308", marginBottom: 4 }}>{stats.suspiciousIps}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#eab308", opacity: 0.8 }}>Şüpheli IP</div>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(59, 130, 246, 0.4)",
            backgroundColor: "rgba(59, 130, 246, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#3b82f6", marginBottom: 4 }}>{stats.failedLoginsToday}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#3b82f6", opacity: 0.8 }}>Başarısız Giriş (Bugün)</div>
        </div>
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid rgba(168, 85, 247, 0.4)",
            backgroundColor: "rgba(168, 85, 247, 0.08)",
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "#a855f7", marginBottom: 4 }}>{stats.adminActionsToday}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#a855f7", opacity: 0.8 }}>Admin İşlem (Bugün)</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", padding: "4px 0" }}>
        {[
          { key: "ip-log", label: "📋 IP Log" },
          { key: "suspicious", label: "⚠️ Suspicious IPs" },
          { key: "rate-limit", label: "🚫 Rate Limit Events" },
          { key: "alerts", label: "🔔 System Alerts" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key as TabType);
              setSearchQuery("");
              setSelectedItem(null);
            }}
            style={{
              flex: "1 1 auto",
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 700,
              borderRadius: 8,
              border: activeTab === tab.key ? "1px solid rgba(59, 130, 246, 0.5)" : "1px solid rgba(156, 163, 175, 0.3)",
              backgroundColor: activeTab === tab.key ? "rgba(59, 130, 246, 0.15)" : "var(--bg2)",
              color: activeTab === tab.key ? "#3b82f6" : "var(--text)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="🔍 Ara (IP, kullanıcı, endpoint, mesaj...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 12px",
            fontSize: 14,
            borderRadius: 8,
            border: "1px solid rgba(156, 163, 175, 0.3)",
            backgroundColor: "var(--bg2)",
            color: "var(--text)",
          }}
        />
      </div>

      {/* Main Content Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.1fr)", gap: 16 }}>
        {/* Left: Table */}
        <div>
          <div
            style={{
              overflowX: "auto",
              borderRadius: 10,
              border: "1px solid rgba(156, 163, 175, 0.2)",
              backgroundColor: "var(--bg2)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ backgroundColor: "rgba(156, 163, 175, 0.08)" }}>
                  {activeTab === "ip-log" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>IP</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Kullanıcı</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İşlem</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Endpoint</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Status</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Zaman</th>
                    </>
                  )}
                  {activeTab === "suspicious" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>IP</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Skor</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İstek</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Başarısız</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Durum</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İlk Görülme</th>
                    </>
                  )}
                  {activeTab === "rate-limit" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>IP</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Kullanıcı</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Endpoint</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Limit Tipi</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Zaman</th>
                    </>
                  )}
                  {activeTab === "alerts" && (
                    <>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Tip</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Seviye</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Mesaj</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Durum</th>
                      <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Zaman</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
                      {searchQuery ? "Arama sonucu bulunamadı" : "Henüz kayıt yok"}
                    </td>
                  </tr>
                )}
                {activeTab === "ip-log" &&
                  (filteredData as IpLogEntry[]).map((log) => (
                    <tr
                      key={log._id}
                      onClick={() => setSelectedItem(log)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === log._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== log._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== log._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12 }}>{log.ip}</td>
                      <td style={{ padding: 12 }}>{log.username || "-"}</td>
                      <td style={{ padding: 12 }}>{log.action}</td>
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12, color: "var(--muted)" }}>{log.endpoint}</td>
                      <td style={{ padding: 12, color: log.statusCode >= 400 ? "#ef4444" : "#22c55e", fontWeight: 600 }}>{log.statusCode}</td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>{formatDate(log.timestamp)}</td>
                    </tr>
                  ))}
                {activeTab === "suspicious" &&
                  (filteredData as SuspiciousIp[]).map((ip) => (
                    <tr
                      key={ip._id}
                      onClick={() => setSelectedItem(ip)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === ip._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== ip._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== ip._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12 }}>{ip.ip}</td>
                      <td style={{ padding: 12, color: ip.suspicionScore >= 8 ? "#ef4444" : "#eab308", fontWeight: 700 }}>{ip.suspicionScore}</td>
                      <td style={{ padding: 12 }}>{ip.requestCount}</td>
                      <td style={{ padding: 12, color: "#ef4444", fontWeight: 600 }}>{ip.failedAttempts}</td>
                      <td style={{ padding: 12 }}>
                        {ip.blacklisted ? (
                          <span style={{ color: "#ef4444", fontSize: 12 }}>🚫 Blacklist</span>
                        ) : ip.whitelisted ? (
                          <span style={{ color: "#22c55e", fontSize: 12 }}>✅ Whitelist</span>
                        ) : (
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>{formatDate(ip.firstSeen)}</td>
                    </tr>
                  ))}
                {activeTab === "rate-limit" &&
                  (filteredData as RateLimitEvent[]).map((event) => (
                    <tr
                      key={event._id}
                      onClick={() => setSelectedItem(event)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === event._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== event._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== event._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12 }}>{event.ip}</td>
                      <td style={{ padding: 12 }}>{event.username || "-"}</td>
                      <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12, color: "var(--muted)" }}>{event.endpoint}</td>
                      <td style={{ padding: 12, color: "#ef4444", fontWeight: 600 }}>{event.limitType}</td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>{formatDate(event.timestamp)}</td>
                    </tr>
                  ))}
                {activeTab === "alerts" &&
                  (filteredData as SystemAlert[]).map((alert) => (
                    <tr
                      key={alert._id}
                      onClick={() => setSelectedItem(alert)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedItem?._id === alert._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedItem?._id !== alert._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?._id !== alert._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontWeight: 600 }}>{alert.type}</td>
                      <td style={{ padding: 12 }}>{getSeverityBadge(alert.severity)}</td>
                      <td style={{ padding: 12 }}>{alert.message}</td>
                      <td style={{ padding: 12 }}>
                        {alert.resolved ? (
                          <span style={{ color: "#22c55e", fontSize: 12 }}>✅ Çözüldü</span>
                        ) : (
                          <span style={{ color: "#ef4444", fontSize: 12 }}>⚠️ Aktif</span>
                        )}
                      </td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>{formatDate(alert.timestamp)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div
          style={{
            borderRadius: 10,
            border: "1px solid rgba(156, 163, 175, 0.2)",
            backgroundColor: "var(--bg2)",
            padding: 20,
            height: "fit-content",
            position: "sticky",
            top: 24,
          }}
        >
          {!selectedItem ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Sol taraftan bir kayıt seç</div>
              <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>Detaylı bilgiler burada görünecek</div>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(156, 163, 175, 0.2)" }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 8 }}>
                  {activeTab === "ip-log" && "📋 IP Log Detayı"}
                  {activeTab === "suspicious" && "⚠️ Şüpheli IP Detayı"}
                  {activeTab === "rate-limit" && "🚫 Rate Limit Event"}
                  {activeTab === "alerts" && "🔔 System Alert"}
                </h2>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--muted)", wordBreak: "break-all" }}>
                  {selectedItem._id}
                </div>
              </div>

              {/* Dynamic Content Based on Type */}
              <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
                {/* Common IP Field */}
                {"ip" in selectedItem && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>IP:</span>
                    <span style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 600 }}>{selectedItem.ip}</span>
                  </div>
                )}

                {/* Type-Specific Fields */}
                {activeTab === "ip-log" && "statusCode" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Kullanıcı:</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{"username" in selectedItem ? selectedItem.username || "-" : "-"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>İşlem:</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{"action" in selectedItem ? selectedItem.action : "-"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Endpoint:</span>
                      <span style={{ fontSize: 12, fontFamily: "monospace", color: "var(--muted)" }}>
                        {"endpoint" in selectedItem ? selectedItem.endpoint : "-"}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Status:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: selectedItem.statusCode >= 400 ? "#ef4444" : "#22c55e" }}>
                        {selectedItem.statusCode}
                      </span>
                    </div>
                  </>
                )}

                {activeTab === "suspicious" && "suspicionScore" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Skor:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: selectedItem.suspicionScore >= 8 ? "#ef4444" : "#eab308" }}>
                        {selectedItem.suspicionScore} / 10
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>İstekler:</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{"requestCount" in selectedItem ? selectedItem.requestCount : "-"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Başarısız:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>
                        {"failedAttempts" in selectedItem ? selectedItem.failedAttempts : "-"}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Durum:</span>
                      <span>
                        {"blacklisted" in selectedItem && selectedItem.blacklisted ? (
                          <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 12 }}>🚫 Blacklist</span>
                        ) : "whitelisted" in selectedItem && selectedItem.whitelisted ? (
                          <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 12 }}>✅ Whitelist</span>
                        ) : (
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>Normal</span>
                        )}
                      </span>
                    </div>
                  </>
                )}

                {activeTab === "rate-limit" && "limitType" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Kullanıcı:</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{"username" in selectedItem ? selectedItem.username || "-" : "-"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Endpoint:</span>
                      <span style={{ fontSize: 12, fontFamily: "monospace", color: "var(--muted)" }}>
                        {"endpoint" in selectedItem ? selectedItem.endpoint : "-"}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Limit Tipi:</span>
                      <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 700 }}>{selectedItem.limitType}</span>
                    </div>
                  </>
                )}

                {activeTab === "alerts" && "severity" in selectedItem && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Tip:</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{"type" in selectedItem ? selectedItem.type : "-"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Seviye:</span>
                      {getSeverityBadge(selectedItem.severity)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Durum:</span>
                      {"resolved" in selectedItem && selectedItem.resolved ? (
                        <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 12 }}>✅ Çözüldü</span>
                      ) : (
                        <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 12 }}>⚠️ Aktif</span>
                      )}
                    </div>
                  </>
                )}

                {/* Timestamp */}
                {"timestamp" in selectedItem && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Zaman:</span>
                    <span style={{ fontSize: 12 }}>{formatDate(selectedItem.timestamp)}</span>
                  </div>
                )}
              </div>

              {/* Additional Sections */}
              {"userAgent" in selectedItem && selectedItem.userAgent && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🌐 User Agent</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 11,
                      fontFamily: "monospace",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedItem.userAgent}
                  </div>
                </div>
              )}

              {"message" in selectedItem && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📝 Mesaj</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 13,
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedItem.message}
                  </div>
                </div>
              )}

              {(("metadata" in selectedItem && selectedItem.metadata) ||
                ("details" in selectedItem && selectedItem.details) ||
                ("notes" in selectedItem && selectedItem.notes)) && (
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                    {"notes" in selectedItem ? "📝 Notlar" : "🔖 Metadata"}
                  </h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 11,
                      fontFamily: "monospace",
                      wordBreak: "break-word",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    {"notes" in selectedItem && selectedItem.notes ? (
                      selectedItem.notes
                    ) : (
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(
                          "metadata" in selectedItem ? selectedItem.metadata : "details" in selectedItem ? selectedItem.details : {},
                          null,
                          2
                        )}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setSelectedItem(null)}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  marginTop: 20,
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: 8,
                  border: "1px solid rgba(156, 163, 175, 0.4)",
                  backgroundColor: "rgba(156, 163, 175, 0.1)",
                  color: "#9ca3af",
                  cursor: "pointer",
                }}
              >
                Kapat
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
